import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Upload, X, GripVertical, Edit3 } from 'lucide-react';
import { uploadGalleryPhoto } from '@/utils/storage';
import { PractitionerPhoto } from '@/types/practitioner-photos';
import UnifiedFileUpload from '@/components/ui/UnifiedFileUpload';

interface PhotoGalleryManagerProps {
  practitionerId: string;
  onUpdate?: () => void;
}

export default function PhotoGalleryManager({ practitionerId, onUpdate }: PhotoGalleryManagerProps) {
  const [photos, setPhotos] = useState<PractitionerPhoto[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isUploading, setIsUploading] = useState(false);
  const [editingDescription, setEditingDescription] = useState<string | null>(null);
  const [tempDescription, setTempDescription] = useState('');
  const { toast } = useToast();

  useEffect(() => {
    fetchPhotos();
  }, [practitionerId]);

  const fetchPhotos = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('practitioner_photos')
        .select('*')
        .eq('practitioner_id', practitionerId)
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setPhotos(data || []);
    } catch (error) {
      console.error('Error fetching photos:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to load photos.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    if (photos.length + files.length > 5) {
      toast({
        variant: 'destructive',
        title: 'Upload limit exceeded',
        description: 'You can only have up to 5 photos in your gallery.',
      });
      return;
    }

    setIsUploading(true);

    try {
      for (const file of Array.from(files)) {
        // Validate file type
        if (!file.type.startsWith('image/')) {
          toast({
            variant: 'destructive',
            title: 'Invalid file',
            description: 'Please upload only image files.',
          });
          continue;
        }

        // Upload to storage
        const uploadResult = await uploadGalleryPhoto('practitioner.photo', practitionerId, file);
        const photoUrl = uploadResult.publicUrl;
        
        // Save to database
        const { error } = await supabase
          .from('practitioner_photos')
          .insert({
            practitioner_id: practitionerId,
            photo_url: photoUrl,
            display_order: photos.length,
            photo_type: 'general',
            admin_review_status: 'approved'
          });

        if (error) throw error;
      }

      await fetchPhotos();
      onUpdate?.();
      
      toast({
        title: 'Success',
        description: 'Photos uploaded successfully.',
      });
    } catch (error) {
      console.error('Error uploading photos:', error);
      toast({
        variant: 'destructive',
        title: 'Upload failed',
        description: 'Failed to upload photos. Please try again.',
      });
    } finally {
      setIsUploading(false);
      // Reset input
      event.target.value = '';
    }
  };

  const handleDeletePhoto = async (photoId: string) => {
    try {
      const { error } = await supabase
        .from('practitioner_photos')
        .update({ is_active: false })
        .eq('id', photoId);

      if (error) throw error;

      await fetchPhotos();
      onUpdate?.();
      
      toast({
        title: 'Success',
        description: 'Photo removed from gallery.',
      });
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to remove photo.',
      });
    }
  };

  const handleUpdateDescription = async (photoId: string, description: string) => {
    try {
      const { error } = await supabase
        .from('practitioner_photos')
        .update({ photo_description: description })
        .eq('id', photoId);

      if (error) throw error;

      await fetchPhotos();
      setEditingDescription(null);
      setTempDescription('');
      
      toast({
        title: 'Success',
        description: 'Photo description updated.',
      });
    } catch (error) {
      console.error('Error updating description:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update description.',
      });
    }
  };

  const startEditingDescription = (photo: PractitionerPhoto) => {
    setEditingDescription(photo.id);
    setTempDescription(photo.photo_description || '');
  };

  const cancelEditingDescription = () => {
    setEditingDescription(null);
    setTempDescription('');
  };

  const handleUpdateDisplayOrder = async (photoId: string, newOrder: number) => {
    try {
      const { error } = await supabase
        .from('practitioner_photos')
        .update({ display_order: newOrder })
        .eq('id', photoId);

      if (error) throw error;

      await fetchPhotos();
    } catch (error) {
      console.error('Error updating display order:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to update photo order.',
      });
    }
  };

  if (isLoading) {
    return <div className="text-center py-4">Loading photos...</div>;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Photo Gallery</CardTitle>
        <p className="text-sm text-muted-foreground">
          Upload up to 5 photos to showcase your practice (currently {photos.length}/5)
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Upload Section */}
        <UnifiedFileUpload
          accept="image/*"
          multiple={true}
          maxFiles={5 - photos.length}
          maxSize={20}
          onFilesChange={async (files) => {
            if (files.length === 0) return;
            
            setIsUploading(true);
            try {
              for (const file of files) {
                // Upload to storage
                const uploadResult = await uploadGalleryPhoto('practitioner.photo', practitionerId, file);
                const photoUrl = uploadResult.publicUrl;
                
                // Save to database
                const { error } = await supabase
                  .from('practitioner_photos')
                  .insert({
                    practitioner_id: practitionerId,
                    photo_url: photoUrl,
                    display_order: photos.length,
                    photo_type: 'general',
                    admin_review_status: 'approved'
                  });

                if (error) throw error;
              }

              await fetchPhotos();
              onUpdate?.();
              
              toast({
                title: 'Success',
                description: 'Photos uploaded successfully.',
              });
            } catch (error) {
              console.error('Error uploading photos:', error);
              toast({
                variant: 'destructive',
                title: 'Upload failed',
                description: 'Failed to upload photos. Please try again.',
              });
            } finally {
              setIsUploading(false);
            }
          }}
          disabled={isUploading || photos.length >= 5}
          formatHint="Supports JPG, PNG, WEBP up to 20MB each"
        >
          Click to upload photos or drag and drop
        </UnifiedFileUpload>

        {/* Photos Grid */}
        {photos.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {photos.map((photo, index) => (
              <div key={photo.id} className="border border-border rounded-lg overflow-hidden">
                <div className="relative">
                  <img
                    src={photo.photo_url}
                    alt={photo.photo_description || `Gallery photo ${index + 1}`}
                    className="w-full h-32 object-cover"
                  />
                  <div className="absolute top-2 right-2 flex gap-1">
                    <Button
                      variant="secondary"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => startEditingDescription(photo)}
                    >
                      <Edit3 className="h-3 w-3" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      className="h-6 w-6 p-0"
                      onClick={() => handleDeletePhoto(photo.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="absolute top-2 left-2 flex items-center gap-1">
                    <GripVertical className="h-4 w-4 text-muted-foreground" />
                    <span className="text-xs bg-background/80 px-1 rounded">
                      {index + 1}
                    </span>
                  </div>
                </div>
                
                <div className="p-3">
                  {editingDescription === photo.id ? (
                    <div className="space-y-2">
                      <Textarea
                        value={tempDescription}
                        onChange={(e) => setTempDescription(e.target.value)}
                        placeholder="Add a description..."
                        className="text-sm"
                        rows={2}
                      />
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          onClick={() => handleUpdateDescription(photo.id, tempDescription)}
                        >
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={cancelEditingDescription}
                        >
                          Cancel
                        </Button>
                      </div>
                    </div>
                  ) : (
                    <p className="text-sm text-foreground">
                      {photo.photo_description || (
                        <span className="text-muted-foreground italic">
                          Click edit to add description
                        </span>
                      )}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {photos.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No photos uploaded yet.</p>
            <p className="text-sm">Upload some photos to showcase your practice!</p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}